import { ref, computed, watch, inject, onMounted } from 'vue'
import { defineStore } from 'pinia'
import { groupBy, orderBy } from 'lodash'

import { WSInstance, WSResponse } from '@/services/ws/utils/types'
import type { AIMessage, AIConversation, PaginatedResponse } from '@types'
import type { AIChatMessageData } from '../utils/types'
import { AIType } from '../utils/enums'

import api from '@/store/api'

import { TRANSACTION_TYPE } from '@/entities/transactions/utils/const'
import { generateId } from '@/entities/utils/helpers'
import { useUISettings } from '@/hooks/uiSettings'
import { removeStoreListItem } from '@/store/utils/helpers'
import { dateTimeToString, stringToDateTime } from '@/helpers/dates'
import { handleCatchedError } from '@/helpers/common'
import { destroyTread, fetchThread, handleResponse, prepareMessages } from '../utils/helpers'
import { useAIPortfolioContext } from '../utils/context'

import { useUserStore } from '@/store/user'

export const useAIAgentStore = defineStore('ai-agent', () => {
  // INIT
  const userStore = useUserStore()

  const {
    accountsList,
    currenciesList,
    accountTypesFor
  } = useAIPortfolioContext()

  const getTmpThreadId = () => `tmp_${generateId()}`

  const currentThreadId = useUISettings<string>(['ai-agent-thread-id'], getTmpThreadId())

  const ws = inject<WSInstance>('ws')

  const messages = ref<AIMessage[]>([])
  const conversations = ref<AIConversation[]>([])

  const loading = ref(false)
  const initFlag = ref(false)

  const errorMessage = ref<string>()

  // GETTERS
  const getErrorMessage = computed(() => errorMessage.value)

  const getMessages = computed(() => messages.value)

  const getConversations = computed(() => groupBy(orderBy(conversations.value, 'last_updated', 'desc'), item => {
    return dateTimeToString(stringToDateTime(item.last_updated), { relative: true })
  }))

  const getCurrentThreadId = computed(() => currentThreadId.value)

  const isNewThread = computed(() => getCurrentThreadId.value.startsWith('tmp_'))

  // SETTERS

  const setErrorMessage = (data?: string) => {
    errorMessage.value = data
  }

  const setMessages = (data: AIMessage[]) => {
    messages.value = data
  }

  const pushMessage = (data: AIMessage) => {
    messages.value = [...messages.value, data]
  }

  const setConversations = (data: AIConversation[]) => {
    conversations.value = data
  }

  const setConversationLastUpdated = (threadId: string) => {
    const conversation = conversations.value.find(item => item.thread_id === threadId)
    if (!conversation) return
    conversation.last_updated = new Date().toISOString()
  }

  const setCurrentThreadId = (data: string) => {
    setErrorMessage(undefined)
    currentThreadId.value = data
  }

  // ACTIONS

  const fetch = async (threadId: string) => {
    loading.value = true
    try {
      const result = await fetchThread(threadId)
      setMessages(result)
    } catch (e) {
      handleCatchedError(e as string)
    } finally {
      loading.value = false
    }
  }

  const fetchConversations = async () => {
    try {
      const result = await api.get<PaginatedResponse<AIConversation>>(
        'ai_conversations',
      )
      setConversations(result.data.data)
    } catch (e) {
      handleCatchedError(e as string)
    } finally {
      initFlag.value = true
    }
  }

  const destroy = async (threadId: string) => {
    try {
      await destroyTread(threadId)
      if (getCurrentThreadId.value === threadId) {
        reset()
      }
      removeStoreListItem<AIConversation>(conversations.value, threadId, 'thread_id')
      if (getCurrentThreadId.value === threadId) {
        reset(false)
      }
    } catch (e) {
      handleCatchedError(e as string)
    }
  }

  const submit = async (content: string, pushToStore = true) => {
    const threadId = isNewThread.value ? undefined : getCurrentThreadId.value

    const { messages, message } = prepareMessages(content)
    pushToStore && pushMessage(message)

    const dynamicContext = {
      accounts_list: accountsList.value,
      currencies_list: `"${currenciesList.value.join('\", \"')}"`,
      add_position_accounts_types: JSON.stringify(accountTypesFor(TRANSACTION_TYPE.ADD_POSITION)),
      subscribe_and_commit_accounts_types: JSON.stringify(accountTypesFor(TRANSACTION_TYPE.SUBSCRIBE_AND_COMMIT)),
      subscribe_and_fund_accounts_types: JSON.stringify(accountTypesFor(TRANSACTION_TYPE.SUBSCRIBE_AND_FUND)),
      add_liability_accounts_types: JSON.stringify(accountTypesFor(TRANSACTION_TYPE.ADD_LIABILITY)),
    }

    const messageData: AIChatMessageData = {
      ai_type: AIType.AGENT,
      messages,
      thread_id: threadId,
      dynamic_context: dynamicContext,
    }

    loading.value = true
    ws?.send('ai', 'chat', getCurrentThreadId.value, messageData)
  }

  const retry = () => {
    const content = messages.value.at(-1)?.content
    setErrorMessage(undefined)
    if (!content) return
    submit(content, false)
  }

  const reset = (withConversations = true) => {
    messages.value = []
    loading.value = false
    setCurrentThreadId(getTmpThreadId())
    if (withConversations) {
      conversations.value = []
    }
  }

  watch(
    () => userStore.isAuthenticated,
    value => {
      if (!value) return
      ws?.subscribe(
        'ai',
        'chat',
        async (message: WSResponse<string>, requestId: string) => {
          // Every AI assistant shares this channel, so an answer meant for the
          // asset wizard or the dashboard must not clear this store's spinner.
          if (requestId !== getCurrentThreadId.value) return

          try {
            handleResponse(requestId, getCurrentThreadId.value, message, setErrorMessage, setCurrentThreadId, pushMessage, setConversationLastUpdated)
          } catch (e) {
            handleCatchedError(e as string)
          } finally {
            loading.value = false
          }
        },
      )
    },
    { immediate: true },
  )

  onMounted(() => {
    if (isNewThread.value) return
    fetch(getCurrentThreadId.value)
  })

  return {
    initFlag,
    loading,

    fetch,
    fetchConversations,

    getMessages,
    getConversations,
    getCurrentThreadId,
    getErrorMessage,

    setCurrentThreadId,

    submit,
    retry,

    destroy,

    reset,
  }
})
